/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import input from "../../../../assets/styles/components/input.module.css";
import {
  getMoviment,
  patchMoviments,
  postMoviments,
} from "../../../../services/cashFlow";
import { type movimentType } from "../../../../types/cashFlow";
import { formatDate } from "../../../../utils/useMask";
import Button from "../../../../components/Button/Button";
import ModalGeneric from "../../../../components/ModalGeneric/ModalGeneric";
import styles from "./Modal.module.css";
import SelectChartOfAccounts from "./SelectChartOfAccounts";
import SelectCostCenter from "./SelectCostCenter";
import SelectFinanceAccount from "./SelectFinanceAccount";

type prop = {
  onclose: () => void;
  title: string;
  uid?: string;
  initialData?: Partial<movimentType>;
  onSuccessExternal?: (data_movimentacao?: string | null) => void;
  lockEntryType?: boolean;
  isPdv?: boolean;
  defaultOrigin?: string;
};

const Modal = ({
  onclose,
  title,
  uid,
  initialData,
  onSuccessExternal,
  lockEntryType = false,
  isPdv = false,
  defaultOrigin = "PDV",
}: prop) => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["getMoviment", uid],
    queryFn: () => getMoviment(uid || ""),
    enabled: !!uid,
  });

  const { mutate } = useMutation({
    mutationFn: async (data: movimentType) => {
      if (uid) {
        const res = await patchMoviments({
          uid_financeiro_movimentacao: uid,
          ...data,
        });
        return res;
      } else {
        const res = await postMoviments(data);
        return res;
      }
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ["getMovimentsCashFlow"] });
      if (isPdv) {
        queryClient.invalidateQueries({ queryKey: ["getMovimentsCashFlowPDV"] });
      }

      toast.success(response?.message);
      if (response) {
        resetValue();

        if (onSuccessExternal) {
          onSuccessExternal(variables.data_movimentacao);
        }

        onclose();
      }
    },
    onError: (err: any) => {
      toast.error(err.message);
    },
  });

  useEffect(() => {
    if (!data || Object.keys(data).length === 0) {
      resetValue();
      return;
    } else {
      const {
        data_movimentacao,
        valor,
        tipo,
        descricao,
        uid_conta_financeira,
        uid_financeiro_lancamento,
        uid_plano_contas,
        uid_centro_custo,
        origem,
      } = data;

      setValue({
        data_movimentacao: formatDate(data_movimentacao) || null,
        valor: valor,
        tipo,
        descricao,
        uid_conta_financeira,
        uid_financeiro_lancamento,
        uid_plano_contas,
        uid_centro_custo,
        origem,
      });
    }
  }, [data]);

  const [value, setValue] = useState<movimentType>({
    data_movimentacao: new Date().toISOString().split("T")[0],
    valor: "",
    tipo: "entrada",
    descricao: "",
    uid_conta_financeira: "",
    uid_financeiro_lancamento: null,
    uid_plano_contas: "",
    uid_centro_custo: "",
    origem: "manual"
  });

  useEffect(() => {
    if (initialData && !uid) {
      setValue((prev) => ({
        ...prev,
        ...initialData,
        tipo: initialData.tipo || prev.tipo,
        valor: initialData.valor ? initialData.valor : prev.valor,
        data_movimentacao: initialData.data_movimentacao ? formatDate(initialData.data_movimentacao)! : prev.data_movimentacao,
      }));
    }
  }, [initialData, uid]);

  const resetValue = () =>
    setValue({
      data_movimentacao: new Date().toISOString().split("T")[0],
      valor: "",
      tipo: "entrada",
      descricao: "",
      uid_conta_financeira: "",
      uid_financeiro_lancamento: null,
      uid_plano_contas: "",
      uid_centro_custo: "",
      origem: "manual",
    });

  const handleInput = (e: string, valueName: string) => {
    const v = e;

    if (valueName === "descricao" && v.length > 200) return;

    setValue((prev) => ({ ...prev, [valueName]: v }));
  };

  const inputCustom = (
    label: string,
    valueName: string,
    type?: React.HTMLInputTypeAttribute,
    placeholder?: string,
    isDisabled?: boolean
  ) => {
    return (
      <label>
        {label}
        <input
          type={type}
          className={input.input}
          value={value[valueName as keyof typeof value] as string}
          onChange={(e) => handleInput(e.target.value, valueName)}
          placeholder={placeholder}
          disabled={isDisabled}
          style={
            isDisabled
              ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
              : {}
          }
        />
      </label>
    );
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();

    const differentValues = Object.keys(value || {})
      .filter(
        (k) => data?.[k as keyof typeof data] !== value[k as keyof typeof value]
      )
      .reduce(
        (acc, k) => ({ ...acc, [k]: value?.[k as keyof typeof value] }),
        {} as Partial<movimentType>
      );

    if (Object.values(differentValues).length === 0) return;

    const payload = { ...value };

    if (isPdv) {
      (payload as any).origem = defaultOrigin;
    }

    mutate(payload);
  };

  return (
    <ModalGeneric
      title={title}
      onClick={onclose}
      contentClass={styles.contentMoviment}>
      <form className={styles.containerMoviment} onSubmit={submit}>
        <div className={`${styles.textArea} ${styles.firstLine}`}>
          {inputCustom("Data", "data_movimentacao", "Date")}

          <label>
            Tipo
            <select
              className={input.input}
              value={value.tipo}
              onChange={(e) => handleInput(e.target.value, "tipo")}
              disabled={lockEntryType}
              style={
                lockEntryType
                  ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                  : {}
              }
            >
              <option value="entrada">Entrada</option>
              <option value="saida">Saída</option>
              <option value="transferencia">Transferência</option>
            </select>
          </label>
          <SelectFinanceAccount
            handleChangeAccount={(e) => handleInput(e, "uid_conta_financeira")}
            value={value.uid_conta_financeira}
          />

          <label>
            Valor
            <NumericFormat
              className={input.input}
              placeholder="R$ 0,00"
              thousandSeparator="."
              decimalSeparator=","
              prefix="R$ "
              decimalScale={2}
              fixedDecimalScale
              allowNegative={false}
              value={value.valor}
              onValueChange={(values) => {
                const { floatValue } = values;
                setValue((prev) => ({
                  ...prev,
                  valor: floatValue !== undefined ? String(floatValue) : ""
                }));
              }}
            />
          </label>
        </div>

        <div className={`${styles.textArea} ${styles.firstLine}`}>
          <div className={styles.fieldGroup} style={{ flex: 1.5 }}>
            <SelectChartOfAccounts
              value={value.uid_plano_contas}
              handleChangeChartOfAccount={(e) => handleInput(e, "uid_plano_contas")}
              type="ambos"
            />
            {isPdv && <span style={{ fontSize: '10px', color: '#888' }}>Opcional no PDV</span>}
          </div>

          <div className={styles.fieldGroup} style={{ flex: 1.5 }}>
            <SelectCostCenter
              value={value.uid_centro_custo || ""}
              handleChangeCostCenter={(e) => handleInput(e, "uid_centro_custo")}
            />
            {isPdv && <span style={{ fontSize: '10px', color: '#888' }}>Opcional no PDV</span>}
          </div>
        </div>

        <label>
          Descrição
          <textarea
            value={value.descricao}
            className={input.input}
            placeholder="Descrição"
            onChange={(e) =>
              handleInput(e.target.value, "descricao")
            }></textarea>
        </label>

        <div className={styles.save}>
          <Button type="submit" variant="contain">
            Salvar
          </Button>
        </div>
      </form>
    </ModalGeneric>
  );
};

export default Modal;